import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

/**
 * Counts up from 0 to `value` when the element scrolls into view.
 *
 * Props:
 *   value: number (target)
 *   duration: seconds (default 1.6)
 *   decimals: number (default 0)
 *   prefix / suffix: string
 *   formatter: (n) => string (overrides prefix/suffix/decimals)
 */
export const AnimatedNumber = ({
  value,
  duration = 1.6,
  decimals = 0,
  prefix = "",
  suffix = "",
  formatter,
  className,
  "data-testid": testId,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setDisplay(value);
      return;
    }
    const start = performance.now();
    const from = 0;
    const to = Number(value) || 0;
    let raf;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const p = Math.min(1, (now - start) / (duration * 1000));
      const current = from + (to - from) * ease(p);
      setDisplay(current);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, reduce]);

  const rendered = formatter
    ? formatter(display)
    : `${prefix}${display.toFixed(decimals)}${suffix}`;

  return (
    <motion.span
      ref={ref}
      className={className}
      data-testid={testId}
      aria-live="polite"
    >
      {rendered}
    </motion.span>
  );
};

export default AnimatedNumber;
